import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConnectError, createClient } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import { CheckoutService } from 'proto/gen/ts/shirt/v1/checkout_pb';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../routes';
import { useAuthStore } from '../store/authStore';
import { formatPrice } from '../utils/format';
import { formatAddress } from '../utils/orderTypes';
import { transport } from '../utils/transport';
const checkoutClient = createClient(CheckoutService, transport);
export default function OrderDetailPage() {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();
    const guestEmail = searchParams.get('email');
    const isAuthenticated = useAuthStore((s)=>s.isAuthenticated);
    const { data, isLoading, isError, error } = useQuery({
        queryKey: [
            'orderDetail',
            orderId,
            guestEmail
        ],
        queryFn: ()=>checkoutClient.lookupGuestOrder({
                orderId: orderId ?? '',
                email: guestEmail ?? ''
            }),
        enabled: !!orderId
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 animate-pulse space-y-4",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "h-8 bg-stone-100 w-1/3"
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "h-40 bg-stone-50"
                })
            ]
        });
    }
    if (isError || !data) {
        const msg = error instanceof ConnectError ? error.message : 'Order not found. Check your order ID and email.';
        return /*#__PURE__*/ _jsxs("div", {
            className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center",
            children: [
                /*#__PURE__*/ _jsx("h1", {
                    className: "font-display text-4xl text-stone-900 uppercase tracking-wide mb-4",
                    children: "Order Not Found"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-stone-500 mb-6",
                    children: msg
                }),
                /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.orderLookup,
                    className: "inline-block bg-stone-900 text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider",
                    children: "Try Again"
                })
            ]
        });
    }
    let items = [];
    try {
        if (data.itemsJson) items = JSON.parse(data.itemsJson);
    } catch  {
    // ignore
    }
    const shipping = data.shippingAddress ? formatAddress(data.shippingAddress) : null;
    const statusColor = data.status === 'paid' ? 'bg-blue-100 text-blue-700' : data.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-stone-100 text-stone-600';
    const fulfillmentLabel = data.fulfillmentStatus ? data.fulfillmentStatus === 'pending' ? 'Preparing your order' : data.fulfillmentStatus === 'packed' ? 'Packed — ready to ship' : data.fulfillmentStatus === 'shipped' ? 'Shipped' : data.fulfillmentStatus === 'delivered' ? 'Delivered' : data.fulfillmentStatus === 'cancelled' ? 'Cancelled' : data.fulfillmentStatus : '';
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16",
        children: [
            isAuthenticated && /*#__PURE__*/ _jsx(Link, {
                to: ROUTES.accountOrders,
                className: "text-sm text-stone-500 hover:text-stone-900 mb-4 inline-block",
                children: "← Back to Orders"
            }),
            /*#__PURE__*/ _jsx("h1", {
                className: "font-display text-4xl text-stone-900 uppercase tracking-wide mb-8",
                children: "Order Details"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-6",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border-2 border-stone-100 p-6 space-y-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between items-start",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs text-stone-400 uppercase tracking-wider",
                                                children: "Order ID"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm font-mono text-stone-900 mt-1",
                                                children: data.id
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `px-3 py-1 text-xs font-semibold uppercase tracking-wider ${statusColor}`,
                                        children: data.status
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "grid grid-cols-2 gap-4",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs text-stone-400 uppercase tracking-wider",
                                                children: "Email"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-stone-900 mt-1",
                                                children: data.email
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs text-stone-400 uppercase tracking-wider",
                                                children: "Date"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-sm text-stone-900 mt-1",
                                                children: data.createdAt ? new Date(data.createdAt).toLocaleDateString() : ''
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs text-stone-400 uppercase tracking-wider",
                                                children: "Total"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-lg font-display text-stone-900 mt-1",
                                                children: formatPrice(Number(data.totalCents))
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    fulfillmentLabel && /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border-2 border-stone-100 p-6",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3",
                                children: "Fulfillment"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-stone-900",
                                children: fulfillmentLabel
                            }),
                            data.trackingNumber && /*#__PURE__*/ _jsxs("div", {
                                className: "mt-3 pt-3 border-t border-stone-100 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-stone-500 uppercase tracking-wider",
                                        children: "Tracking"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm font-mono text-stone-900 break-all",
                                        children: data.trackingNumber
                                    }),
                                    data.trackingCarrier && /*#__PURE__*/ _jsx("p", {
                                        className: "text-xs text-stone-500 uppercase",
                                        children: data.trackingCarrier
                                    }),
                                    data.trackingUrl && /*#__PURE__*/ _jsx("a", {
                                        href: data.trackingUrl,
                                        target: "_blank",
                                        rel: "noopener noreferrer",
                                        className: "inline-block mt-2 text-xs underline text-stone-700 hover:text-stone-900",
                                        children: "Track package"
                                    })
                                ]
                            })
                        ]
                    }),
                    shipping && /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border-2 border-stone-100 p-6",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3",
                                children: "Shipping"
                            }),
                            shipping.name && /*#__PURE__*/ _jsx("p", {
                                className: "text-sm font-semibold text-stone-900",
                                children: shipping.name
                            }),
                            shipping.lines.map((line)=>/*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-stone-600",
                                    children: line
                                }, line))
                        ]
                    }),
                    items.length > 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border-2 border-stone-100 p-6",
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-sm font-semibold text-stone-900 uppercase tracking-wider mb-3",
                                children: "Items"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "space-y-4",
                                children: items.map((item)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "flex gap-4 border-b border-stone-100 pb-4 last:border-0 last:pb-0",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "w-16 h-16 flex-shrink-0 bg-stone-100 overflow-hidden",
                                                children: item.image_url && /*#__PURE__*/ _jsx("img", {
                                                    src: item.image_url,
                                                    alt: item.name,
                                                    className: "w-full h-full object-cover"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex-1 min-w-0",
                                                children: [
                                                    /*#__PURE__*/ _jsx("p", {
                                                        className: "text-sm font-semibold text-stone-900",
                                                        children: item.name
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center justify-between mt-1",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("span", {
                                                                className: "text-xs text-stone-500",
                                                                children: [
                                                                    "Qty: ",
                                                                    item.quantity
                                                                ]
                                                            }),
                                                            item.price_cents > 0 && /*#__PURE__*/ _jsx("span", {
                                                                className: "text-sm text-stone-900",
                                                                children: formatPrice(item.price_cents * item.quantity)
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    }, item.product_id))
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-10 text-center",
                children: /*#__PURE__*/ _jsx(Link, {
                    to: ROUTES.shop,
                    className: "inline-block bg-stone-900 text-white px-6 py-3 text-xs font-semibold uppercase tracking-wider",
                    children: "Continue Shopping"
                })
            })
        ]
    });
}
